<template>
	<el-dialog v-dialogDrag class="chat-record" title="语音录制" :visible.sync="visible" width="600px" :before-close="onClose">
		<div v-show="mode == 'RECORD'">
			<div class="tip">{{ stateTip }}</div>
			<div>时长: {{ state == 'STOP' ? 0 : duration }}s / 60s</div>
		</div>
		<audio v-show="mode == 'PLAY'" :src="url" controls ref="audio" @ended="onStopAudio()"></audio>
		<el-divider content-position="center"></el-divider>
		<el-row class="btn-group">
			<el-button round type="primary" v-show="state == 'STOP'" @click="onStartRecord()">开始录音</el-button>
			<el-button round type="warning" v-show="state == 'RUNNING'" @click="onPauseRecord()">暂停录音</el-button>
			<el-button round type="primary" v-show="state == 'PAUSE'" @click="onResumeRecord()">继续录音</el-button>
			<el-button round type="danger" v-show="state == 'RUNNING' || state == 'PAUSE'" @click="onCompleteRecord()">
				结束录音</el-button>
			<el-button round type="success" v-show="state == 'COMPLETE' && mode != 'PLAY'" @click="onPlayAudio()">播放录音
			</el-button>
			<el-button round type="warning" v-show="state == 'COMPLETE' && mode == 'PLAY'" @click="onStopAudio()">停止播放
			</el-button>
			<el-button round type="primary" v-show="state == 'COMPLETE'" @click="onRestartRecord()">重新录音</el-button>
			<el-button round type="primary" v-show="state == 'COMPLETE'" :loading="sending" :disabled="sending"
				@click="onSendRecord()">立即发送</el-button>
		</el-row>
	</el-dialog>
</template>

<script>
import Recorder from 'js-audio-recorder';
import { uploadVoice } from '../../api/upload.js';
import { encode } from '../../utils/silk/index.js';

export default {
	name: 'chatRecord',
	props: {
		visible: {
			type: Boolean
		}
	},
	data() {
		return {
			rc: this.createRecorder(),
			state: 'STOP', // STOP、RUNNING、PAUSE、COMPLETE
			stateTip: "未开始",
			mode: 'RECORD', // RECORD 、PLAY
			sending: false, // 转码/上传中，防止重复发送
			starting: false, // 录音启动中，防止快速双击重复 start
			duration: 0,
			url: "",
			audioUrl: "" // 试听 blob URL，关闭时回收
		}
	},
	methods: {
		// 创建录音实例：微信语音标准采样率 24000Hz 单声道 16bit
		// （js-audio-recorder 参数名为 sampleBits/numChannels），并绑定 60 秒上限自动结束
		createRecorder() {
			let rc = new Recorder({ sampleRate: 24000, sampleBits: 16, numChannels: 1 });
			rc.onprocess = (duration) => {
				// 录音过程中实时刷新时长（响应式，模板展示）
				this.duration = Math.floor(duration);
				// 微信语音上限 60 秒，自动结束录音
				if (duration >= 60 && this.state !== 'COMPLETE') {
					this.onCompleteRecord();
					this.$message.info('已达到 60 秒上限，自动结束录音');
				}
			};
			return rc;
		},
		onClose() {
			// 转码/上传中禁止关闭，防止误关后消息仍发出
			if (this.sending) {
				this.$message.warning('语音正在转码上传，请稍候');
				return;
			}
			// 关闭前清除数据
			this.rc.destroy();
			this.rc = this.createRecorder();
			this.starting = false;
			// 停止试听并回收 blob URL（模板 audio 元素与数据源一致）
			if (this.$refs.audio) {
				this.$refs.audio.pause();
			}
			if (this.audioUrl) {
				URL.revokeObjectURL(this.audioUrl);
				this.audioUrl = "";
			}
			this.url = "";
			this.mode = 'RECORD';
			this.state = 'STOP';
			this.stateTip = '未开始';
			this.duration = 0;
			this.$emit("close");
		},
		onStartRecord() {
			// 防抖：start() 为异步操作，避免快速双击重复启动
			if (this.starting) {
				return;
			}
			this.starting = true;
			this.duration = 0;
			let rc = this.rc;
			rc.start().then((stream) => {
				// 弹窗已关闭或已重新录音时，丢弃过期回调
				if (this.rc !== rc) {
					return;
				}
				this.state = 'RUNNING';
				this.stateTip = "正在录音...";
			}).catch(error => {
				this.state = 'STOP';
				this.stateTip = '未开始';
				this.$message.error(error && error.message ? error.message : String(error));
			}).finally(() => {
				this.starting = false;
			});
		},
		onPauseRecord() {
			this.rc.pause();
			this.state = 'PAUSE';
			this.stateTip = "已暂停录音";
		},
		onResumeRecord() {
			this.rc.resume();
			this.state = 'RUNNING';
			this.stateTip = "正在录音...";
		},
		onCompleteRecord() {
			this.rc.pause();
			this.state = 'COMPLETE';
			this.stateTip = "已结束录音";
			this.duration = Math.floor(this.rc.duration);
		},
		onPlayAudio() {
			let wav = this.rc.getWAVBlob();
			// 通过响应式 url 赋值驱动模板 :src 绑定，避免直接改 DOM 被重渲染覆盖
			if (this.audioUrl) {
				URL.revokeObjectURL(this.audioUrl);
			}
			this.audioUrl = URL.createObjectURL(wav);
			this.url = this.audioUrl;
			this.$nextTick(() => {
				this.$refs.audio.play();
			});
			this.mode = 'PLAY';
		},
		onStopAudio() {
			this.$refs.audio.pause();
			this.mode = 'RECORD';
		},
		onRestartRecord() {
			this.rc.destroy();
			this.rc = this.createRecorder();
			this.starting = true;
			let rc = this.rc;
			rc.start().then(() => {
				// 启动期间弹窗被关闭则丢弃过期回调
				if (this.rc !== rc) {
					return;
				}
				this.state = 'RUNNING';
				this.mode = 'RECORD';
				this.stateTip = "正在录音...";
			}).catch(error => {
				this.state = 'STOP';
				this.mode = 'RECORD';
				this.stateTip = '未开始';
				this.$message.error(error && error.message ? error.message : String(error));
			}).finally(() => {
				this.starting = false;
			});
		},
		async onSendRecord() {
			// 微信语音上限 60 秒（留 0.5s 容差：60 秒自动结束时 duration 可能略超 60）
			if (this.rc.duration > 60.5) {
				this.$message.warning('语音时长不能超过 60 秒，请重新录音');
				return;
			}
			// 语音过短无法编码发送
			if (this.rc.duration < 1) {
				this.$message.warning('录音太短，请重新录音');
				return;
			}
			this.sending = true;
			this.stateTip = "正在转码上传...";
			try {
				// wav 转 silk（传入 sampleRate=0 自动识别 wav 采样率）
				let wav = this.rc.getWAVBlob();
				let res = await encode(await wav.arrayBuffer(), 0);
				// 以实际编码时长为准校验：四舍五入后超过 60 秒的录音直接拒绝，
				// 避免 60~60.5s 容差导致真实时长超微信上限（此处拒绝不会产生上传）
				if (Math.round(res.duration / 1000) > 60) {
					this.$message.warning('语音时长不能超过 60 秒，请重新录音');
					return;
				}
				let silkBlob = new Blob([res.data], { type: 'application/octet-stream' });
				let name = new Date().getTime() + '.silk';
				let formData = new window.FormData();
				formData.append('file', silkBlob, name);
				// 上传到后端（返回 { path, size, name }）
				let data = await uploadVoice(formData);
				this.$emit("send", {
					// 取整后钳制到 60，避免四舍五入越过后端上限校验
					duration: Math.min(60, Math.max(1, Math.round(res.duration / 1000))),
					url: data.path,
					size: silkBlob.size
				});
				// 先解除发送中标记，再走统一关闭清理（否则会被 onClose 的 sending 拦截）
				this.sending = false;
				this.onClose();
			} catch (error) {
				this.$message.error('语音转码或上传失败：' + (error && error.message ? error.message : error));
			} finally {
				this.sending = false;
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.chat-record {

	.tip {
		font-size: 18px;
	}

	.btn-group {
		margin-bottom: 20px;
	}
}
</style>
